import { delivered, handled, toCollect, type Route } from '@day';
import { useStore } from '../app/store';
import { go, hide, openRoute, openShare, openStop, planWith, ui } from '../app/ui';
import { openDialog } from '../app/dialogs';
import { routeMenu } from '../app/menus';
import { dayWords, length, money, plural, streetOf, time } from '../app/words';
import { keyOf, routeTitle, type Model } from '../data/model';
import { routeDay, routesSections, type RoutesRow } from '../data/routesPage';
import { Av, EmptyHelp, More, Segs, nearly } from '../ui/bits';
import { Icon } from '../ui/Icon';
import { Sec, StopRow, eyeOf, sentLine } from './Live';

/**
 * Routes (board `routes-web`): every route of the day by where it's got to, beside the map, with a search over all their
 * stops. Opening one goes to its own view, as from Live; Back comes here.
 */
export function RoutesList({ m }: { m: Model }) {
  const s = useStore(ui);
  const country = m.business?.country ?? null;
  // A past day: its routes from the last 60 days already read, to look at only.
  const past = s.rday && s.rday < m.today ? s.rday : null;
  const routes = past ? (m.history ?? []).filter((r) => routeDay(r, m.zone) === past) : dayRoutes(m);
  const all = routesSections(routes, '', country).flatMap((x) => x.rows.map((r) => r.r));
  const q = s.routesQ;
  const ql = q.trim().toLowerCase();
  const secs = routesSections(routes, q, country);
  const hits = secs.flatMap((x) => x.rows.flatMap((r) => r.hits));
  const shut = (id: string) => s.closed.has(`routes-${id}`);
  const setQ = (v: string) => ui.set({ routesQ: v });
  const head = (
    <div className="lhead">
      <div className="dayt">
        <button className="title daypick" data-menu="rday:" data-tip="Another day" aria-label="Pick a day">
          {past ? dayWords(past) : `Today, ${dayWords(m.today)}`}
          <Icon name="caretDown" size="sm" />
        </button>
      </div>
      {all.length > 0 && <div className="cap note">{dayLine(m, all, !!past)}</div>}
      {all.length > 0 && (
        <div className="field" style={{ marginTop: 10 }}>
          <Icon name="magnifyingGlass" />
          <input
            id="routesq"
            placeholder="Find a stop in any route"
            aria-label="Find a stop in any route"
            value={q}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && hits.length === 1) openStop(keyOf(hits[0]), 'day');
              if (e.key === 'Escape' && q) {
                e.stopPropagation();
                setQ('');
              }
            }}
          />
          {q && (
            <button className="iconbtn sm" aria-label="Clear the search" data-tip="Clear the search" onClick={() => setQ('')}>
              <Icon name="x" />
            </button>
          )}
        </div>
      )}
    </div>
  );
  if (!all.length) {
    const waiting = m.waiting.length;
    return (
      <>
        {head}
        <EmptyHelp
          title={past ? 'No routes that day.' : 'No routes today yet.'}
          tries={past ? [{ icon: 'arrowUUpLeft', label: 'Back to today', act: 'today' }] : [
            { icon: 'path', label: 'Plan a route', note: 'Pick the stops and who takes them', act: 'plan' },
            ...(waiting && m.people.length ? [{ icon: 'arrowsSplit', label: `Share out the ${plural(waiting, 'waiting stop')}`, note: 'Across your couriers, a route each', act: 'share' }] : []),
            ...(!m.people.length ? [{ icon: 'userPlus', label: 'Invite a courier', note: 'They join with a code in the Routr app', act: 'invite' }] : []),
          ]}
          onTry={(a) => {
            if (a === 'today') ui.set({ rday: null });
            else if (a === 'plan') planWith();
            else if (a === 'share') openShare(m.waiting.map((w) => w.id), m.people.map((p) => p.uid));
            else openDialog({ kind: 'invite' });
          }}
        />
      </>
    );
  }
  if (ql && !secs.length) {
    const stops = all.flatMap((r) => r.stops);
    return (
      <>
        {head}
        <EmptyHelp
          q={q.trim()}
          tries={[
            ...nearly(ql, stops, (x) => `${x.address} ${x.name ?? ''}`).map((x) => ({ icon: 'mapPin', label: streetOf(x), note: [m.person(x.riderId)?.first, `stop ${x.n}`, x.name].filter(Boolean).join(' · '), act: `stop:${keyOf(x)}` })),
            { icon: 'x', label: 'Clear the search', act: 'clear' },
            { icon: 'mapPin', label: 'Search the Stops page', note: 'Every stop today, as a table', act: 'stops' },
            { icon: 'path', label: 'Plan a route', note: 'For a stop not on one yet', act: 'plan' },
          ]}
          onTry={(a) => {
            if (a.startsWith('stop:')) openStop(a.slice(5), 'day');
            else if (a === 'stops') {
              go('stops');
              ui.set({ q: q.trim(), tab: 'all', courier: null, sel: new Set() });
            } else if (a === 'plan') planWith();
            else setQ('');
          }}
        />
      </>
    );
  }
  return (
    <>
      {head}
      {secs.map((sec) => (
        <div key={sec.id}>
          <Sec id={`routes-${sec.id}`} label={sec.label} n={sec.rows.length} eye={past ? undefined : eyeOf(sec.rows.map((x) => x.r.id), s.hidden)} closed={shut(sec.id)} />
          {!shut(sec.id) && (
            <div className="rows">
              {sec.rows.map((x) => (
                <RouteRow key={x.r.id} m={m} x={x} searching={!!ql} past={!!past} />
              ))}
            </div>
          )}
        </div>
      ))}
      <div style={{ height: 28 }} />
    </>
  );
}

/** Today's routes: the day's own, and a courier's running or finished today that began on an earlier day. */
function dayRoutes(m: Model): Route[] {
  return [...m.routes, ...m.people.flatMap((p) => [...(p.day?.running ? [p.day.running] : []), ...(p.day?.finished ?? [])])];
}

/** "6 routes and 48 stops · the last should be done about 5:40 PM"; a past day's, when the last ended. */
function dayLine(m: Model, routes: Route[], past: boolean): string {
  const stops = routes.reduce((a, r) => a + r.stops.length, 0);
  const finishes = past ? [] : m.people.map((p) => (p.day?.running ? p.day.finishAt : null)).filter((x): x is number => x != null);
  const ends = routes.map((r) => (r.state === 'done' || r.state === 'ended' ? (r.completedAt ?? r.endedAt) : null)).filter((x): x is number => x != null);
  const waiting = past ? 0 : routes.filter((r) => r.state === 'waiting').length;
  const last = finishes.length
    ? `the last should be done about ${time(Math.max(...finishes), m.zone)}`
    : waiting
      ? `${plural(waiting, 'route')} still to start`
      : ends.length
        ? `the last ended ${time(Math.max(...ends), m.zone)}`
        : null;
  return [`${plural(routes.length, 'route')} and ${plural(stops, 'stop')}`, last].filter(Boolean).join(' · ');
}

/**
 * A route in a line of words: "Finishing 2:45 PM · 4 of 8 delivered · 12.0 km · ₦8,500 to collect", "Finished 12:37 PM ·
 * 6 of 6 delivered · 14.8 km", "Ended early 1:10 PM · 3 of 8 delivered · 5 left"; a sent one as Live says it.
 */
export function routeWords(m: Model, r: Route): string {
  if (r.state === 'waiting' || r.state === 'declined') return sentLine(m, r);
  const p = m.person(r.riderId);
  const n = r.stops.length;
  const done = `${delivered(r)} of ${n} delivered`;
  const km = r.km ? length(r.km * 1000) : null;
  if (r.state === 'running') {
    const at = p?.day?.running?.id === r.id ? p.day.finishAt : null;
    const late = m.looks.filter((x) => x.kind === 'late' && x.route?.id === r.id).length;
    const due = r.stops.some((x) => x.deliverBy != null && !handled(x));
    const left = r.stops.filter((x) => !handled(x));
    const owed = left.reduce((a, x) => a + toCollect(x), 0);
    const cur = left.find((x) => x.currency)?.currency ?? m.currency;
    return [at != null ? `Finishing ${time(at, m.zone)}` : null, late ? `late for ${plural(late, 'stop')}` : due ? 'on time so far' : null, done, km, owed ? `${money(owed, cur)} to collect` : null].filter(Boolean).join(' · ');
  }
  const end = r.completedAt ?? r.endedAt;
  const left = r.stops.filter((x) => !handled(x)).length;
  if (left) return [`Ended early${end != null ? ` ${time(end, m.zone)}` : ''}`, done, `${left} left`].join(' · ');
  const timed = r.stops.filter((x) => x.state === 'done' && x.deliverBy != null && x.at != null);
  const lateOnes = timed.filter((x) => x.at! > x.deliverBy!).length;
  const onTime = timed.length ? (lateOnes ? `${lateOnes} late` : 'all on time') : null;
  return [`Finished${end != null ? ` ${time(end, m.zone)}` : ''}`, done, km, onTime].filter(Boolean).join(' · ');
}

/** The row's actions, shown on hover: those of the route's own menu that suit its state, so both do the same. */
const ROW_ACTS = ['Open the route', 'Fit the map to it', 'Give it to someone else', 'Send it to someone', 'Take it back'];

/**
 * A route: the courier's photo, the route's name and theirs in ink, where it's got to in words; its stops found below.
 * Under the pointer, the map fades the other routes. A past day's is only to look at, and not on the map: nothing on it
 * changes, and only its menu (copy it, print it) shows.
 */
function RouteRow({ m, x, searching, past }: { m: Model; x: RoutesRow; searching: boolean; past: boolean }) {
  const s = ui.get();
  const { r, hits } = x;
  const p = m.person(r.riderId);
  const off = s.hidden.has(r.id);
  const acts = routeMenu(r.id).filter((i) => i.label && ROW_ACTS.includes(i.label));
  return (
    <div className="rgroup">
      <div
        className={`rrow rpg${s.route === r.id ? ' on' : ''}`}
        data-ctx={`route:${r.id}`}
        style={{ '--c': p?.hex ?? '#5E594F', ...(off && !past ? { opacity: 0.5 } : {}) } as React.CSSProperties}
        onClick={() => openRoute(r.id)}
        onMouseEnter={past ? undefined : () => ui.set({ hover: r.id })}
        onMouseLeave={past ? undefined : () => ui.get().hover === r.id && ui.set({ hover: null })}
      >
        <div className="who">
          <span className="avw">
            <Av p={p} />
            {r.state === 'done' && (
              <span className="okb">
                <Icon name="check" />
              </span>
            )}
          </span>
          <div className="grow">
            <div className="nm">
              {routeTitle(r, m.zone)} <span className="rt">· {p?.first ?? 'A courier'}</span>
            </div>
            <div className="meta wrap">{searching ? `${hits.length} of its ${plural(r.stops.length, 'stop')} ${hits.length === 1 ? 'matches' : 'match'}` : routeWords(m, r)}</div>
          </div>
        </div>
        {r.state === 'running' && !searching && <Segs stops={r.stops} />}
        {past ? <div className="racts" onClick={(e) => e.stopPropagation()}><More menu={`route:${r.id}`} /></div> : <div className="racts" onClick={(e) => e.stopPropagation()}>
          {acts.map((a) => (
            <button
              key={a.label}
              className={`iconbtn sm${a.danger ? ' neg' : ''}`}
              data-tip={a.label}
              aria-label={a.label}
              data-menu={a.sub ? `give:${r.id}` : undefined}
              onClick={a.sub ? undefined : a.run}
            >
              <Icon name={a.icon ?? 'path'} />
            </button>
          ))}
          <button
            className="iconbtn sm"
            data-tip={off ? 'Show on the map' : 'Hide from the map'}
            aria-label={off ? 'Show on the map' : 'Hide from the map'}
            onClick={(e) => {
              e.stopPropagation();
              hide(r.id);
            }}
          >
            <Icon name={off ? 'eyeSlash' : 'eye'} />
          </button>
          <More menu={`route:${r.id}`} />
        </div>}
      </div>
      {hits.length > 0 && (
        <div className="stops hits">
          {hits.map((h) => (
            <StopRow key={h.id} m={m} s={h} filtered />
          ))}
        </div>
      )}
    </div>
  );
}
